import { formatCompactInstant, toFiniteAmount } from '../../utils/formatMoney'

const REFUND_REASONS = {
  expired_request: 'Solicitud de reserva expirada',
  lobby_cancelled: 'Viaje cancelado',
  companion_leave: 'Viaje abandonado',
  cancelled_request: 'Solicitud de reserva cancelada',
  rejected_request: 'Solicitud de reserva rechazada',
  admin_companion_removed: 'Acompañante removido por Admin',
  admin_lobby_cancelled: 'Viaje cancelado por Admin',
  join_failed: 'Pagó y no quedó en el viaje',
  admin_manual_refund: 'Reembolso manual (Admin)',
  approved_not_joined: 'Pagó pero no quedó en el viaje',
  paid_by_provider: 'Ya estaba pagado con Mercado Pago',
  admin_refund_remainder: 'Restante de un reembolso parcial (Admin)'
}

const REFUNDED_STATUSES = ['refunded', 'cancelled']

const AWAITING_CHOICE = 'awaiting_choice'

const KNOWN_STATES = [AWAITING_CHOICE, 'processing', 'completed', 'stuck', 'manual']

export const PENDING_REFUND_STATES = ['processing', 'stuck', 'manual']

export const ADMIN_PARTIAL_REFUND_REASON = 'Reembolso 50% por soporte'

export const getRefundReason = reason => {
  if (typeof reason !== 'string') return reason
  return REFUND_REASONS[reason.trim()] || reason
}

export const initiatorType = initiatedBy => {
  if (typeof initiatedBy === 'string') return initiatedBy || null
  return typeof initiatedBy?.type === 'string' && initiatedBy.type ? initiatedBy.type : null
}

export const describeRefundReason = ({ reason, partial, initiator } = {}) => {
  if (partial === true && initiatorType(initiator) === 'admin') return ADMIN_PARTIAL_REFUND_REASON
  if (reason === null || reason === undefined || reason === '') return null
  return getRefundReason(reason)
}

const normalizeState = state => {
  if (state === null || state === undefined || state === '') return 'completed'
  return KNOWN_STATES.includes(state) ? state : 'unknown'
}

const normalizeMethod = (method, state) => {
  if (state === AWAITING_CHOICE) return null
  return method === 'wallet' ? 'wallet' : 'original'
}

const inferPartial = (explicitPartial, status, state) =>
  explicitPartial === true || (status === 'approved' && state === 'completed')

const positiveAmount = value => {
  const amount = toFiniteAmount(value)
  return amount !== null && amount > 0 ? amount : 0
}

const rawRefundAmount = (refund, state) => {
  const hasWalletPart = positiveAmount(refund.wallet_amount) > 0
  const base = toFiniteAmount(hasWalletPart ? refund.amount : refund.amount_refunded_to_payer ?? refund.amount)
  if (base === null || state === AWAITING_CHOICE) return base
  return base + positiveAmount(refund.wallet_leg?.amount)
}

const fromRawRefund = transaction => {
  const refund = transaction.refund
  const state = normalizeState(refund.state)
  return {
    method: normalizeMethod(refund.method, state),
    state,
    partial: inferPartial(refund.partial, transaction.status, state),
    amount: rawRefundAmount(refund, state),
    currency:
      refund.currency ??
      transaction.metadata?.currency ??
      transaction.preference?.metadata?.currency ??
      transaction.currency,
    reason: refund.refund_reason,
    initiator: initiatorType(refund.initiated_by),
    date: refund.date_created,
    deadline: refund.choice_deadline,
    originalFailed: Boolean(refund.original_failed_at),
    remainderState: refund.remainder?.state ?? null
  }
}

const hasFlatRefund = transaction =>
  transaction.refund_method != null ||
  transaction.refund_state != null ||
  transaction.refund_reason != null ||
  transaction.refund_date != null ||
  REFUNDED_STATUSES.includes(transaction.status)

const fromFlatRefund = transaction => {
  const state = normalizeState(transaction.refund_state)
  return {
    method: normalizeMethod(transaction.refund_method, state),
    state,
    partial: inferPartial(transaction.refund_partial, transaction.status, state),
    amount: toFiniteAmount(transaction.refund_amount ?? transaction.refunded_amount),
    currency: transaction.currency,
    reason: transaction.refund_reason,
    initiator: initiatorType(transaction.refund_initiated_by),
    date: transaction.refund_date,
    deadline: transaction.refund_choice_deadline,
    originalFailed: state === AWAITING_CHOICE && transaction.refund_choice_deadline === null,
    remainderState: transaction.refund_remainder_state ?? null
  }
}

export const getRefundInfo = transaction => {
  if (!transaction || typeof transaction !== 'object') return null
  if (transaction.refund && typeof transaction.refund === 'object') return fromRawRefund(transaction)
  if (hasFlatRefund(transaction)) return fromFlatRefund(transaction)
  return null
}

const keepTogether = text => text.replace(/ /g, '\u00a0')

const awaitingChoiceLabel = info => {
  if (info.originalFailed) return `Esperando elección · ${keepTogether('el medio original falló')}`
  const deadline = formatCompactInstant(info.deadline, info.currency)
  return deadline ? `Esperando elección · ${keepTogether(`vence ${deadline}`)}` : 'Esperando elección'
}

export const getRefundBadge = info => {
  if (!info) return null
  if (info.state === AWAITING_CHOICE) return { label: awaitingChoiceLabel(info), tone: 'amber', wrap: true }
  if (info.state === 'processing') return { label: 'Reembolso en proceso', tone: 'amber' }
  if (info.state === 'stuck') return { label: 'Reembolso trabado', tone: 'alert' }
  if (info.state === 'manual') return { label: 'Reembolso manual', tone: 'neutral' }
  if (info.state === 'unknown') return { label: 'Reembolso sin confirmar', tone: 'neutral' }
  if (info.remainderState === 'processing') return { label: 'Restante en proceso', tone: 'amber' }
  if (info.method === 'wallet') {
    return { label: info.partial ? 'Reembolso parcial a saldo' : 'Reembolsado a saldo', tone: 'emerald' }
  }
  if (info.partial) return { label: 'Reembolso parcial', tone: 'neutral' }
  return { label: 'Reembolsado', tone: 'coral' }
}

export const getRefundDestination = info => {
  if (!info || !PENDING_REFUND_STATES.includes(info.state)) return null
  return info.method === 'wallet' ? 'a saldo Viatik' : 'al medio de pago'
}
